import { DateMoment } from '@app/core/common/date-moment';
import { Helper } from '@app/core/common/helper';
import { BaseModel } from '@app/core/models/base.model';
import { SubscriptionType } from '@app/modules/organization/constants/organization.constant';
import { Exclude, Expose, Transform, Type } from 'class-transformer';
import { DateTime } from 'luxon';

export type PlanStatus = 'draft' | 'activated' | 'deactivated';

export interface SubscriptionPlanFeatureInterface {
    id: number;
    planId: number;
    slug: string;
    name: string;
    description: string;
    value: any;
    attrs: any;
    createdAt: string;
}

export interface SubscriptionPlanAttrsInterface {
    maxUsers: number;
    allowedSubscription: Array<typeof SubscriptionType>;
    group: string;
    intro: string[];
    photo: string;
    isPopular: boolean;
}

export interface SubscriptionPlanInterface {
    id: number;
    name: string;
    slug: string;
    description: string;
    price: number;
    attrs: any;
    uuid: string;
    createdAt: string;
    updatedAt: string;
    features: Array<SubscriptionPlanFeatureInterface>;
    prices: {
        month: number;
        year: number;
    };
    status: PlanStatus;
    isPublished: boolean;
    trialPeriod: number;
}

export interface SubscriptionOfOrgInterface {
    id?: number;
    organizationId: number;
    planId: number;
    name: string;
    description: string;
    startAt: DateTime | string;
    endAt: DateTime | string;
    metadata?: {
        package: SubscriptionType;
    };
    createdAt?: string;
    createdById?: number;
    updatedAt?: string;
    uuid?: string;
    plan?: SubscriptionPlanModel;
}



export class SubscriptionPlanAttrsModel
    extends BaseModel
    implements SubscriptionPlanAttrsInterface {
    maxUsers = 0;
    allowedSubscription: typeof SubscriptionType[] = [];
    group: string;
    intro: string[];
    photo: string;
    isPopular: boolean;
}

export class SubscriptionPlanFeatureModel
    extends BaseModel
    implements SubscriptionPlanFeatureInterface {
    id: number;
    planId: number;
    slug: string;
    name: string;
    description: string;
    value: any;
    attrs: any;
    createdAt: string;
}

export class SubscriptionPlanModel
    extends BaseModel
    implements SubscriptionPlanInterface {
    id: number;
    name: string;
    slug: string;
    description: string;
    price: number;
    @Type(() => SubscriptionPlanAttrsModel)
    attrs: SubscriptionPlanAttrsInterface;
    uuid: string;
    createdAt: string;
    updatedAt: string;
    @Type(() => SubscriptionPlanFeatureModel)
    features: SubscriptionPlanFeatureInterface[] = [];
    status: PlanStatus;
    trialPeriod: number;
    prices: {
        month: number;
        year: number;
    };
    isPublished: boolean;

    @Expose()
    get isSettingPrice(): boolean {
        return (this.prices?.month > 0 && this.prices?.year > 0);
    }

    @Expose()
    get pricePerMonthOfYear(): number {
        if (this.prices && this.prices?.year) {
            return this.prices.year / 12;
        }

        return 0;
    }
}

export class SubscriptionOfOrgModel extends BaseModel implements SubscriptionOfOrgInterface {
    id?: number;
    organizationId: number;
    planId: number;
    name: string;
    description: string;
    @Transform((val) => (val ? DateMoment.date(val) : val))
    @Transform(
        (val) =>
            val && DateMoment.isMoment(val) ? (val as DateTime).toISO() : val,
        { toPlainOnly: true }
    )
    startAt: DateTime;
    @Transform(
        (val) =>
            val && DateMoment.isMoment(val) ? (val as DateTime).toISO() : val,
        { toPlainOnly: true }
    )
    endAt: DateTime;
    metadata?: { package: SubscriptionType; };
    createdAt?: string;
    createdById?: number;
    updatedAt?: string;
    uuid?: string;
    @Type(() => SubscriptionPlanModel)
    plan?: SubscriptionPlanModel = new SubscriptionPlanModel();


    @Expose()
    @Exclude({ toPlainOnly: true })
    get type(): SubscriptionType {
        return this.metadata?.package;
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get isExpired(): boolean {
        return DateMoment.current() > DateMoment.date(this.endAt);
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get remainingDays(): number {
        const startDate = DateMoment.date(this.startAt);
        const endDate = DateMoment.date(this.endAt);

        if (DateMoment.isMoment(endDate) && DateMoment.isMoment(startDate)) {
            const isFeatureSub = DateMoment.current() < startDate;
            const days = isFeatureSub ? this.totalSubcriptionDays : DateMoment.dateDiff(DateMoment.current(), endDate, false, 'days') + 1;

            return days < 0 ? 0 : Math.round(days);
        }

        return 0;
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get totalSubcriptionDays(): number {
        if (this.endAt && this.startAt) {
            return Math.round(DateMoment.dateDiff(this.startAt, this.endAt, false, 'days')) + 1;
        }

        return 0;
    }

    @Expose()
    @Exclude({ toPlainOnly: true })
    get percentUsage() {
        if (this.isExpired) {
            return 100;
        }

        const percent = (1 - (this.remainingDays / this.totalSubcriptionDays)) * 100;

        return Helper.round(percent);
    }
}
